import React, { useRef, useEffect, useState } from "react";
import { useManualQuery, useMutation } from "graphql-hooks";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  IconButton,
  InputLabel,
  TextField,
  Button,
  Paper,
  Divider,
  Avatar,
  Tooltip,
  Card,
  CardMedia,
  CardHeader,
  CardContent,
  CardActions,
  MenuItem,
  Select,
  FormControl,
  Drawer,
  InputBase,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AuthWrapper from "./AuthWrapper";

import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

import FloatingMenu from "./components/FloatingMenu";
import AlertPopup from "./components/AlertPopup";
import ProductCard from "./components/ProductCard";
import UserCard from "./components/UserCard";

import { AGE_GROUP_QUERY,
         BRANDS_QUERY,
         CATEGORY_QUERY,
         COLORS_QUERY,
         CONDITION_QUERY,
         COUNTRIES_QUERY,
         FILTER_TOYS,
         MATERIALS_QUERY,
         SEARCH_TOYS,
         SUBCOLORS_QUERY,
         WARNING_QUERY
       } from "./graphQL/search_qry";
import { CREATE_COLLECTION_MUTATION, DELETE_COLLECTION_MUTATION } from "./graphQL/collection_qry";
import placeholderImage from "./assets/r3play-logo-sml.png";

const contentTypesList = ["All", "Toys", "Stories", "Videos"];

const pricesList = [
  { label: "All Prices", value: "All Prices" },
  { label: "$0 to $25", value: "0-25" },
  { label: "$25 to $50", value: "25-50" },
  { label: "$50 to $100", value: "50-100" },
  { label: "$100 to $200", value: "100-200" },
  { label: "$200 & Above", value: "200-" },
];


const FILTER_DEFAULTS = {
    contentType: "Toys",
    searchBrand: "",
    price: "All Prices",
    minPrice: "",
    maxPrice: "",
    ageGroup: "1",
    color: "1",
    subColor: "1",
    material: "1",
    dimensions: "",
    weight: "",
    batteryRequired: "-",
    batteryIncluded: "-",
    assemblyRequired: "-",
    noOfPlayers: "",
    gameDuration: "",
    warnings: "1",
    conditions: "1",
    manufacturerCountry: "1",
    tags: [],
    brand: [], // for multiple checkboxes
    brandLabel: [], // for multiple checkboxes
}

// ---------- small helpers ----------
const Pill = styled(Chip)(({ theme }) => ({
  borderRadius: 16,
  height: 26,
  background: "#E5E5E5",
  border: "1px solid transparent",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(239, 86, 88, 0.1)",
    borderColor: "#EF5658",
  },
}));

const FavoriteButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isFavorited",
})(({ isFavorited }) => ({
  fontSize: "medium",
  color: isFavorited ? "#EF5658" : "#9e9e9e",
}));

const FilterSelect = styled(Select)(({ theme }) => ({
  height: "56px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C9C9C9",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C9C9C9",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C9C9C9",
  },
}));

const FilterNumberInput = styled(TextField)(({ theme }) => ({
  height: "56px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#C9C9C9",
    },
    "&:hover fieldset": {
      borderColor: "#C9C9C9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#C9C9C9",
    },
  },
}));

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

function timeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();

  const diffMs = now - date;
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (weeks < 5) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  return `${years} year${years > 1 ? "s" : ""} ago`;
}

function formatMonthYear(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

function formatNumber(num) {
  if (num === null || num === undefined) return "0";
  if (num < 1000) return num.toString();
  if (num < 1_000_000) return (num / 1000).toFixed(num >= 10000 ? 0 : 1) + "K";
  if (num < 1_000_000_000) return (num / 1_000_000).toFixed(1) + "M";
  return (num / 1_000_000_000).toFixed(1) + "B";
}

function TagInput({ filters, onFilterChange }) {
  const [tagValue, setTagValue] = useState("");
  const [tags, setTags] = useState(filters.tags || []);  // we expect an array of strings
  const inputRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(filters.tags)) {
      setTags(filters.tags);
    }
  }, [filters.tags]);

  const addTag = () => {
    const newTag = tagValue.trim();
    if (!newTag) return;

    // avoid duplicates
    if (!tags.includes(newTag)) {
      const updatedTags = [...tags, newTag];
      setTags(updatedTags);

      // Update filters.tags as space-separated string
      onFilterChange("tags", updatedTags, "Tags", updatedTags);
    }
    setTagValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  const handleDelete = (tagToRemove) => {
    const updatedTags = tags.filter((t) => t !== tagToRemove);
    console.log(updatedTags);
    setTags(updatedTags);
    onFilterChange("tags", updatedTags, "Tags", updatedTags);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        border: "1px solid rgba(0, 0, 0, 0.23)",
        borderRadius: "4px",
        padding: "4px 8px",
        minHeight: "56px",
        cursor: "text",
      }}
      onClick={() => inputRef.current?.focus()}
    >
      {/* Existing pills */}
      {tags.map((tag, index) => (
        <Chip
          key={index}
          label={tag}
          onDelete={() => handleDelete(tag)}
          sx={{ m: "4px 4px 4px 0" }}
        />
      ))}

      {/* Input inside the same box */}
      <InputBase
        inputRef={inputRef}
        value={tagValue}
        onChange={(e) => setTagValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder=""
        sx={{
          flex: 1,
          minWidth: "120px",
          m: "4px 0",
        }}
      />
    </Box>
  );
}

function generateProducts(searchResults) {
    console.log(searchResults);
    return searchResults.map((item, index) => {
        const randIdx = index % 9; // reuse sample data in rotation

        return {
          id: item.toyId,
          userId: {            
            userId: item.userId.id,
            userUuid: item.userId.userUuid,
            firstName: item.userId.firstName ,
            lastName: item.userId.lastName,
            username: item.userId.username,
            time: timeAgo(item.publishedDate),
            avatarFileName: item.userId.avatarFileName,
            isFollowing: item.userId.isLoggedInUserFollowing,
            listings: item.userId.listings,
            soldToys: item.userId.soldToys,
            ratingNo: item.userId.ratingNo,
            totalNoOfRatings: formatNumber(item.userId.ratingCount),
            joined: formatMonthYear(item.userId.activationDate),
          },
          title: item.title,
          uuid: item.uuid,
          isInCollection: item.isInCollection,
          shareUrl: `${window.location.origin}/toy/${item.uuid}`,
          price: item.sellingPrice,
          oldPrice: item.originalPrice ? item.originalPrice : "",
          category: item.categoryId.title || "Toys",
          subcategory: item.subCategoryId.title || "General",
          brand: item.brandId.title || "Unknown",
          contentType: "Toys",
          likes: item.likeNo,
          isLiked: item.isLiked,
          img: item.toyImages[0]?.fileName || placeholderImage,
          likedUsers: item.toyLikes.map((toyLike, index) => {
            return {id: toyLike.toyLikeId,
              username: toyLike.userId.username,
              avatar: toyLike.userId.avatarFileName,
              isFollowing: toyLike.userId.isLoggedInUserFollowing }
          }),
          shares: item.shareNo,
          isFavorited: Math.random() > 0.5,
          isInCollection: item.isInCollection,
          commentNo: item.commentNo,
          comments: item.toyComments,          
        };
    });
}

function ClosableSelect({ children, ...props }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  return (
    <FilterSelect
      ref={ref}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      MenuProps={{
        disableScrollLock: true,
        PaperProps: {
          ref: dropdownRef, // attach menu DOM here
        },
      }}
      {...props}
    >
      {children}
    </FilterSelect>
  );
}

// ---------- filter UI (used in sidebar & drawer) ----------
function FiltersContent({
  onCatSelect,
  onSubCatSelect,
  selectedCat,
  selectedSubCat,
  activeCategory,
  setActiveCategory,
  openSubCats,
  setOpenSubCats,
  categoriesMap,
  filters,
  onFilterChange,
  onContentTypeChange,
  setProducts,
  handleFilter,
  setAppliedChips,
  selectedChips,
  setNumberofSearchResults,
  setPopupConfig,
  setShowPopup
}) {
  const [expanded, setExpanded] = useState({
    content: true,
    categories: true,
    brand: true,
    price: true,
    others: true,
    tags: true,
  });

  const handleToggle = (key) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [key]: isExpanded }));
  };

  const [filterBrands, setFilterBrands] = useState([]);
  const [fetchBrandsList] = useManualQuery(BRANDS_QUERY, {
    fetchPolicy: "network-only",
  });
  const getBrandsList = async () => {
    const {
      loading: brandsListLoading,
      error: brandsListError,
      data: brandsListData,
    } = await fetchBrandsList();

    if (!brandsListLoading && brandsListData && brandsListData.brands) {
      setFilterBrands(brandsListData.brands.slice(0, 12));
    }
    if(brandsListError){
      console.log(brandsListError)
    } else {
    }
  };

  const [filterColors, setFilterColors] = useState([]);
  const [fetchColorsList] = useManualQuery(COLORS_QUERY, {
    fetchPolicy: "network-only",
  });
  const getColorsList = async () => {
    const {
      loading: colorsListLoading,
      error: colorsListError,
      data: colorsListData,
    } = await fetchColorsList();

    if (!colorsListLoading && colorsListData && colorsListData.colors) {
      setFilterColors(colorsListData.colors);
    }
    if(colorsListError){
      console.log(colorsListError)
    }
  };

  const [filterSubcolors, setFilterSubcolors] = useState([]);
  const [fetchSubcolorsList] = useManualQuery(SUBCOLORS_QUERY, {
    fetchPolicy: "network-only",
  });
  const getSubcolorsList = async () => {
    const {
      loading: subcolorsListLoading,
      error: subcolorsListError,
      data: subcolorsListData,
    } = await fetchSubcolorsList();
    if (!subcolorsListLoading && subcolorsListData && subcolorsListData.subColors) {
      setFilterSubcolors(subcolorsListData.subColors);
    }
    if(subcolorsListError){
      console.log(subcolorsListError)
    }
  };

  const [filterMaterials, setFilterMaterials] = useState([]);
  const [fetchMaterialsList] = useManualQuery(MATERIALS_QUERY, {
    fetchPolicy: "network-only",
  });
  const getMaterialsList = async () => {
    const {
      loading: materialsListLoading,
      error: materialsListError,
      data: materialsListData,
    } = await fetchMaterialsList();
    if (!materialsListLoading && materialsListData && materialsListData.materials) {
      setFilterMaterials(materialsListData.materials);
    }
    if(materialsListError){
      console.log(materialsListError)
    }
  };

  const [ageGroupList, setAgeGroupList] = useState([]);
  const [fetchAgeGroupList] = useManualQuery(AGE_GROUP_QUERY, {
    fetchPolicy: "network-only",
  });
  const getAgeGroupList = async () => {
    const {
      loading: fetchAgeGroupListLoading,
      error: fetchAgeGroupListError,
      data: fetchAgeGroupListData,
    } = await fetchAgeGroupList();
    if (!fetchAgeGroupListLoading && fetchAgeGroupListData && fetchAgeGroupListData.ageGroups) {
      setAgeGroupList(fetchAgeGroupListData.ageGroups);
    }
    if(fetchAgeGroupListError){
      console.log(fetchAgeGroupListError)
    }
  };

  const [warningsList, setWarningsList] = useState([]);
  const [fetchWarningsList] = useManualQuery(WARNING_QUERY, {
    fetchPolicy: "network-only",
  });
  const getWarningsList = async () => {
    const {
      loading: fetchWarningsListLoading,
      error: fetchWarningsListError,
      data: fetchWarningsListData,
    } = await fetchWarningsList();
    if (!fetchWarningsListLoading && fetchWarningsListData && fetchWarningsListData.warnings) {
      setWarningsList(fetchWarningsListData.warnings);
    }
    if(fetchWarningsListError){
      console.log(fetchWarningsListError)
    }
  };

  const [conditionsList, setConditionsList] = useState([]);
  const [fetchConditionsList] = useManualQuery(CONDITION_QUERY, {
    fetchPolicy: "network-only",
  });
  const getConditionsList = async () => {
    const {
      loading: fetchConditionsListLoading,
      error: fetchConditionsListError,
      data: fetchConditionsListData,
    } = await fetchConditionsList();
    if (!fetchConditionsListLoading && fetchConditionsListData && fetchConditionsListData.conditions) {
      setConditionsList(fetchConditionsListData.conditions);
    }
    if(fetchConditionsListError){
      console.log(fetchConditionsListError)
    }
  };

  const [countriesList, setCountriesList] = useState([]);
  const [fetchCountriesList] = useManualQuery(COUNTRIES_QUERY, {
    fetchPolicy: "network-only",
  });
  const getCountriesList = async () => {
    const {
      loading: fetchCountriesListLoading,
      error: fetchCountriesListError,
      data: fetchCountriesListData,
    } = await fetchCountriesList();
    if (!fetchCountriesListLoading && fetchCountriesListData && fetchCountriesListData.manufacturerCountries) {
      setCountriesList(fetchCountriesListData.manufacturerCountries);
    }
    if(fetchCountriesListError){
      console.log(fetchCountriesListError)
    }
  };

  const [tagInput, setTagInput] = useState("");

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const value = tagInput.trim();
      if (!value) return;

      // Avoid duplicates
      if (!filters.tags.includes(value)) {
        onFilterChange("tags", [...filters.tags, value]);
      }

      setTagInput(""); // Clear input
    }
  };

  useEffect(() => {
    getAgeGroupList();
    getBrandsList();
    getColorsList();
    getCountriesList();
    getMaterialsList();
    getSubcolorsList();
    getWarningsList();
    getConditionsList();
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#F9F9F9",
        "@media (max-width: 856px)": {
          paddingLeft: "16px",
          backgroundColor: "#FFF",
        },
      }}
    >
      {/* Content Type */}
      <Accordion
        expanded={expanded.content}
        onChange={handleToggle("content")}
        disableGutters
        sx={{
          boxShadow: "none",
          backgroundColor: "#F9F9F9",
          "&:before": { display: "none" },
          "@media (max-width: 856px)": {
            backgroundColor: "#FFF",
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            expanded.content ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
          sx={{
            paddingLeft: 0,
          }}
        >
          <Typography fontWeight={700}>Content Type</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingLeft: 0,
            "@media (max-width: 856px)": {
              pt: 0,
            },
          }}
        >
          <FormControl fullWidth size="small">
            <ClosableSelect
              value={filters.contentType}
              onChange={(e) => {
                onFilterChange("contentType", e.target.value);
                onContentTypeChange(e.target.value);
              }}
              sx={{
                color: "#9B9B9B",
                borderRadius: "6px",
                "&.Mui-focused fieldset": {
                  borderColor: "#ddd !important",
                },
                "&:hover fieldset": {
                  borderColor: "#ddd !important",
                },
              }}
            >
              {contentTypesList.map((cntType) => (
                <MenuItem key={cntType} value={cntType}>
                  {cntType}
                </MenuItem>
              ))}
            </ClosableSelect>
          </FormControl>
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ width: "calc(100% - 20px)", px: 0, mb: 2 }} />

      {/* Categories */}
      <Accordion
        expanded={expanded.categories}
        onChange={handleToggle("categories")}
        disableGutters
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
          backgroundColor: "#F9F9F9",
          "@media (max-width: 856px)": {
            backgroundColor: "#FFF",
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            expanded.categories ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
          sx={{
            paddingLeft: 0,
          }}
        >
          <Typography variant="body1" fontWeight={700}>
            Categories
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingLeft: 0,
          }}
        >
          <List dense sx={{ pt: 0 }}>
            {categoriesMap.map(category => {
              let subs = category.subcategories;
              const key = category.categoryId; // unique key
              const isOpen = openSubCats[key];

              const selected = selectedSubCat;

              // Check if selected subcat belongs here
              const showSelected =
                selected && selected.category.categoryId === category.categoryId;

              // Determine if the category itself is selected
              const isCategorySelected =
                activeCategory === category &&
                (!selected || selected.category.categoryId !== category.categoryId);

              return (
                <React.Fragment key={category.categoryId}>
                  {/* Category Row */}
                  <ListItem
                    disableGutters
                    sx={{
                      py: 0.4,
                      backgroundColor:
                        activeCategory === category
                          ? "rgba(239, 86, 88, 0.1)"
                          : "transparent",
                      "&:hover": { backgroundColor: "#f1f1f1" },
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // don’t trigger accordion toggle

                        // If a subcategory under this parent is selected, do nothing
                        if (
                          selectedSubCat &&
                          selectedSubCat.category.categoryId === category.categoryId
                        ) {
                          onSubCatSelect(null, null);
                          setActiveCategory(category);
                          onCatSelect(category);
                          return;
                        }

                        if (activeCategory === category) {
                          // clicking again on selected category → unselect
                          setActiveCategory(null);
                          onSubCatSelect(null, null);
                          onCatSelect(null); // clear selection
                        } else {
                          // normal select
                          setActiveCategory(category);
                          onCatSelect(category); // clear selection
                          if (
                            selectedSubCat &&
                            selectedSubCat.category.categoryId !== category.categoryId
                          ) {
                            onSubCatSelect(null, null);
                          }
                        }
                      }}
                    >
                      {isCategorySelected && (
                        <Tooltip
                          title="Unselect this category"
                          placement="top"
                          arrow
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -5],
                                },
                              },
                            ],
                          }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#666",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "500",
                                borderRadius: "4px",
                                padding: "6px 8px",
                                "& .MuiTooltip-arrow": {
                                  color: "#666",
                                },
                              },
                            },
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={true}
                            onClick={(e) => e.stopPropagation()} // prevent row toggle
                            onChange={(e) => {
                              e.stopPropagation(); // prevent triggering row click
                              setActiveCategory(null);
                              onCatSelect(null); // clear selection
                              onSubCatSelect(null, null); // clear selection
                            }}
                            sx={{ p: 0.4, mr: 1 }}
                          />
                        </Tooltip>
                      )}
                      {isCategorySelected ? (
                        <Tooltip
                          title="Click to unselect this category"
                          placement="top"
                          arrow
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -5],
                                },
                              },
                            ],
                          }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#666",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "500",
                                borderRadius: "4px",
                                padding: "6px 8px",
                                "& .MuiTooltip-arrow": {
                                  color: "#666",
                                },
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{ fontSize: 15 }}
                            primary={category.title}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title="Click to select this category"
                          placement="top"
                          arrow
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -5],
                                },
                              },
                            ],
                          }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#666",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: "500",
                                borderRadius: "4px",
                                padding: "6px 8px",
                                "& .MuiTooltip-arrow": {
                                  color: "#666",
                                },
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{ fontSize: 15 }}
                            primary={category.title}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    {/* Expand/Collapse only by arrow icon */}
                    <Box
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // don’t bubble to row
                        const newOpenState = Object.keys(openSubCats).reduce(
                          (acc, k) => {
                            acc[k] = false;
                            return acc;
                          },
                          {}
                        );
                        newOpenState[key] = !isOpen;
                        setOpenSubCats(newOpenState);
                      }}
                    >
                      {isOpen ? (
                        <KeyboardArrowUpIcon
                          fontSize="small"
                          style={{ opacity: 0.7 }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          fontSize="small"
                          style={{ opacity: 0.7 }}
                        />
                      )}
                    </Box>
                  </ListItem>

                  <Collapse
                    in={isOpen || showSelected}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {subs.map(sub => {
                        const isSelected =
                          selected &&
                          selected.category.categoryId === category.categoryId &&
                          selected.subcategoryId === sub.subcategoryId;

                        return (
                          <ListItem
                            key={sub.subcategoryId}
                            button
                            disableGutters
                            onClick={(e) => {
                              e.stopPropagation();

                              if (isSelected) {
                                // if already selected → unselect
                                setActiveCategory(null);
                                //onCatSelect(null);
                                onSubCatSelect(null, null);
                              } else {
                                // normal select
                                onSubCatSelect(category, sub);
                                //onCatSelect(category);
                                setActiveCategory(category);
                              }
                            }}
                            sx={{
                              pl: 1.5,
                              py: 0.3,
                              display: !isOpen && !isSelected ? "none" : "flex", // hide others when collapsed
                            }}
                          >
                            {isSelected && (
                              <Tooltip
                                title="Unselect this subcategory"
                                placement="top"
                                arrow
                                PopperProps={{
                                  modifiers: [
                                    {
                                      name: "offset",
                                      options: {
                                        offset: [0, -5],
                                      },
                                    },
                                  ],
                                }}
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "#666",
                                      color: "white",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      borderRadius: "4px",
                                      padding: "6px 8px",
                                      "& .MuiTooltip-arrow": {
                                        color: "#666",
                                      },
                                    },
                                  },
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={true}
                                  onChange={(e) => {
                                    e.stopPropagation(); // prevent triggering row click
                                    setActiveCategory(null);
                                    onSubCatSelect(null, null); // clear selection
                                  }}
                                  sx={{ p: 0.3, mr: 1 }}
                                />
                              </Tooltip>
                            )}
                            {isSelected ? (
                              <Tooltip
                                title="Click to unselect this subcategory"
                                placement="top"
                                arrow
                                PopperProps={{
                                  modifiers: [
                                    {
                                      name: "offset",
                                      options: {
                                        offset: [0, -5],
                                      },
                                    },
                                  ],
                                }}
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "#666",
                                      color: "white",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      borderRadius: "4px",
                                      padding: "6px 8px",
                                      "& .MuiTooltip-arrow": {
                                        color: "#666",
                                      },
                                    },
                                  },
                                }}
                              >
                                <ListItemText
                                  primaryTypographyProps={{
                                    fontSize: 14,
                                    fontWeight: 400,
                                    color: isSelected ? "#EF5658" : "#777777",
                                  }}
                                  primary={sub.title}
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover .MuiListItemText-primary": {
                                      color: "#EF5658",
                                    },
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title="Click to select this subcategory"
                                placement="top"
                                arrow
                                PopperProps={{
                                  modifiers: [
                                    {
                                      name: "offset",
                                      options: {
                                        offset: [0, -5],
                                      },
                                    },
                                  ],
                                }}
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "#666",
                                      color: "white",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      borderRadius: "4px",
                                      padding: "6px 8px",
                                      "& .MuiTooltip-arrow": {
                                        color: "#666",
                                      },
                                    },
                                  },
                                }}
                              >
                                <ListItemText
                                  primaryTypographyProps={{
                                    fontSize: 14,
                                    fontWeight: 400,
                                    color: isSelected ? "#EF5658" : "#777777",
                                  }}
                                  primary={sub.title}
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover .MuiListItemText-primary": {
                                      color: "#EF5658",
                                    },
                                  }}
                                />
                              </Tooltip>
                            )}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ width: "calc(100% - 20px)", px: 0, mb: 2 }} />

      {/* Brand */}
      <Accordion
        expanded={expanded.brand}
        onChange={handleToggle("brand")}
        disableGutters
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
          backgroundColor: "#F9F9F9",
          "@media (max-width: 856px)": {
            backgroundColor: "#FFF",
          },
        }}
      >
        <AccordionSummary
          sx={{ mt: 2 }}
          expandIcon={
            expanded.brand ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
          sx={{
            paddingLeft: 0,
          }}
        >
          <Typography fontWeight={700}>Brand</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingLeft: 0,
            "@media (max-width: 856px)": {
              pt: 0,
            },
          }}
        >
          <TextField
            fullWidth
            placeholder="Search Brands"
            value={filters.searchBrand}
            sx={{
              height: "46px",
              color: "#9B9B9B",
              "& .MuiOutlinedInput-root": {
                height: "40px",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#212121",
                "& fieldset": {
                  border: "1px solid #ddd",
                  borderRight: "none",
                },
                "&:hover fieldset": {
                  borderColor: "#ddd !important",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ddd !important",
                },
              },
            }}
            onChange={(e) => onFilterChange("searchBrand", e.target.value)}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    sx={{
                      backgroundColor: "#EF5658",
                      color: "white",
                      borderRadius: "0 6px 6px 0",
                      "&:hover": {
                        backgroundColor: "#D94446",
                      },
                    }}
                    edge="end"
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Stack sx={{ mt: 0.5 }}>
            {filterBrands.map((b) => (
              <FormControlLabel
                key={b.brandId}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.brand.includes(b.brandId)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onFilterChange("brand", [...filters.brand, b.brandId], "Brand", [...filters.brandLabel, b.title]);
                      } else {
                        onFilterChange(
                          "brand",
                          filters.brand.filter((x) => x !== b.brandId),
                          "Brand",
                          filters.brandLabel.filter((x) => x !== b.title),
                        );
                      }
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: "14px" }}>{b.title}</Typography>}
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ width: "calc(100% - 20px)", px: 0, mb: 1 }} />

      {/* Price */}
      <Accordion
        expanded={expanded.price}
        onChange={handleToggle("price")}
        disableGutters
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
          backgroundColor: "#F9F9F9",
          "@media (max-width: 856px)": {
            backgroundColor: "#FFF",
          },
        }}
      >
        <AccordionSummary
          sx={{ mt: 2 }}
          expandIcon={
            expanded.price ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
          sx={{
            paddingLeft: 0,
          }}
        >
          <Typography fontWeight={700}>Price</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingLeft: 0,
            "@media (max-width: 856px)": {
              pt: 0,
            },
          }}
        >
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel
              sx={{
                color: "#707070",
                "&.Mui-focused": { color: "#707070" },
              }}
            >
              Select Price
            </InputLabel>
            <ClosableSelect
              label="Select Price"
              value={filters.price}
              onChange={(event, child) => {
                 const value = event.target.value;
                 const label = child.props.children;
                 onFilterChange("price", value, "Price", label);
              }}
            >
              {pricesList.map((price) => (
                <MenuItem value={price.value}>{price.label}</MenuItem>
              ))}
            </ClosableSelect>
          </FormControl>

          {/* Custom Price Label */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Or Enter a Custom Price
          </Typography>

          {/* Min & Max Price Fields */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              label="Min Price"
              id="minPrice"
              value={filters.minPrice}
              onChange={(e) => onFilterChange("minPrice", e.target.value, "Price", e.target.value)}
              InputProps={{ startAdornment: <span>$</span> }}
              fullWidth
              InputLabelProps={{
                height: "56px",
                sx: {
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C9C9C9",
                  },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "#707070",
              }}
            >
              to
            </Typography>
            <TextField
              label="Max Price"
              id="maxPrice"
              value={filters.maxPrice}
              onChange={(e) => onFilterChange("maxPrice", e.target.value, "Price", e.target.value)}
              InputProps={{ startAdornment: <span>$</span> }}
              fullWidth
              InputLabelProps={{
                sx: {
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                },
              }}
              sx={{
                height: "56px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C9C9C9",
                  },
                },
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ width: "calc(100% - 20px)", px: 0, mb: 1 }} />

      {/* Other Filters */}
      <Accordion
        expanded={expanded.others}
        onChange={handleToggle("others")}
        disableGutters
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
          backgroundColor: "#F9F9F9",
          "@media (max-width: 856px)": {
            backgroundColor: "#FFF",
          },
        }}
      >
        <AccordionSummary
          sx={{ mt: 2 }}
          expandIcon={
            expanded.others ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
          sx={{
            paddingLeft: 0,
          }}
        >
          <Typography fontWeight={700}>Other Filters</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingLeft: 0,
          }}
        >
          <Stack spacing={3}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Warnings
              </InputLabel>
              <ClosableSelect
                label="Warnings"
                value={filters.warnings}
                onChange={(e) => {
                  const id = e.target.value;
                  const warning = warningsList.find(c => c.warningId === id);
                  onFilterChange("warnings", id, "Warning", warning?.title);
                }}
              >
                {warningsList.map(warning => (
                  <MenuItem key={warning.warningId} value={warning.warningId}>
                    {warning.title}
                  </MenuItem>
                ))}
              </ClosableSelect>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Condition
              </InputLabel>
              <ClosableSelect
                label="Condition"
                value={filters.conditions}
                onChange={(e) => {
                  const id = e.target.value;
                  const condition = conditionsList.find(c => c.conditionId === id);
                  onFilterChange("conditions", id, "Condition", condition?.title);
                }}
              >
                {conditionsList.map(condition => (
                  <MenuItem key={condition.conditionId} value={condition.conditionId}>
                    {condition.title}
                  </MenuItem>
                ))}
              </ClosableSelect>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Age Group
              </InputLabel>
              <ClosableSelect
                label="Age Group"
                value={filters.ageGroup}
                onChange={(e) => {
                  const id = e.target.value;
                  const ageGroup = ageGroupList.find(c => c.ageGroupId === id);
                  onFilterChange("ageGroup", id, "Age Group", ageGroup?.title);
                }}
              >
                {ageGroupList.map(age => (
                  <MenuItem key={age.ageGroupId} value={age.ageGroupId}>
                    {age.title}
                  </MenuItem>
                ))}
              </ClosableSelect>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Color
              </InputLabel>
              <ClosableSelect
                label="Color"
                value={filters.color}
                onChange={(e) => {
                  const id = e.target.value;
                  const color = filterColors.find(c => c.colorId === id);
                  onFilterChange("color", id, "Color", color?.title);
                }}
              >
                {filterColors.map(color => (
                  <MenuItem key={color.colorId} value={color.colorId}>
                    {color.title}
                  </MenuItem>
                ))}
              </ClosableSelect>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Sub-Color
              </InputLabel>
              <ClosableSelect
                label="Sub-Color"
                value={filters.subColor}
                onChange={(e) => {
                  const id = e.target.value;
                  const subColor = filterSubcolors.find(c => c.subColorId === id);
                  onFilterChange("subColor", id, "Sub-Color", subColor?.title);
                }}
              >
                {filterSubcolors.map(subColor => (
                  <MenuItem key={subColor.subColorId} value={subColor.subColorId}>
                    {subColor.title}
                  </MenuItem>
                ))}
              </ClosableSelect>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Material
              </InputLabel>
              <ClosableSelect
                label="Material"
                value={filters.material}
                onChange={(e) => {
                  const id = e.target.value;
                  const material = filterMaterials.find(c => c.materialId === id);
                  onFilterChange("material", id, "Material", material?.title);
                }}
              >
                {filterMaterials.map((material) => (
                  <MenuItem key={material.materialId} value={material.materialId}>
                    {material.title}
                  </MenuItem>
                ))}
              </ClosableSelect>
            </FormControl>

            <TextField
              label="Dimensions"
              value={filters.dimensions}
              onChange={(e) => onFilterChange("dimensions", e.target.value, "Dimensions", e.target.value)}
              fullWidth
              InputLabelProps={{
                height: "56px",
                sx: {
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C9C9C9",
                  },
                },
              }}
            />

            <TextField
              label="Weight"
              value={filters.weight}
              onChange={(e) => onFilterChange("weight", e.target.value, "Weight", e.target.value)}
              fullWidth
              InputLabelProps={{
                height: "56px",
                sx: {
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C9C9C9",
                  },
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControl fullWidth size="small">
                <Tooltip
                  title="Battery Required"
                  placement="top"
                  arrow
                  PopperProps={{
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -5],
                        },
                      },
                    ],
                  }}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#666",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "500",
                        borderRadius: "4px",
                        padding: "6px 8px",
                        "& .MuiTooltip-arrow": {
                          color: "#666",
                        },
                      },
                    },
                  }}
                >
                  <InputLabel
                    id="battery-label"
                    sx={{
                      color: "#707070",
                      "&.Mui-focused": { color: "#707070" },
                    }}
                  >
                    Battery Required
                  </InputLabel>
                </Tooltip>
                <ClosableSelect
                  labelId="battery-label"
                  id="battery-select"
                  label="Battery Required"
                  value={filters.batteryRequired}
                  onChange={(event, child) => {
                    const value = event.target.value;
                    const label = child.props.children;
                    onFilterChange("batteryRequired", value, "Battery Required", label);
                  }}
                >
                  <MenuItem value="-">Don't Care</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                </ClosableSelect>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel
                  id="battery-lbl"
                  sx={{
                    color: "#707070",
                    "&.Mui-focused": { color: "#707070" },
                  }}
                >
                  Battery Included
                </InputLabel>
                <ClosableSelect
                  labelId="battery-lbl"
                  id="battery-sel"
                  label="Battery Included"
                  value={filters.batteryIncluded}
                  onChange={(event, child) => {
                    const value = event.target.value;
                    const label = child.props.children;
                    onFilterChange("batteryIncluded", value, "Battery Included", label);
                  }}
                >
                  <MenuItem value="-">Don't Care</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                </ClosableSelect>
              </FormControl>
            </Box>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Assembly Required
              </InputLabel>
              <ClosableSelect
                label="Assembly Required"
                value={filters.assemblyRequired}
                onChange={(event, child) => {
                    const value = event.target.value;
                    const label = child.props.children;
                    onFilterChange("assemblyRequired", value, "Assembly Required", label);
                }}
              >
                <MenuItem value="-">Don't Care</MenuItem>
                <MenuItem value="0">No</MenuItem>
                <MenuItem value="1">Yes</MenuItem>
              </ClosableSelect>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <FilterNumberInput
                type="number"
                label="No of Players"
                value={filters.noOfPlayers}
                onChange={(e) => onFilterChange("noOfPlayers", e.target.value, "No. of Players", e.target.value)}
                inputProps={{ min: 1, max: 10 }}
              />
            </FormControl>

            <TextField
              label="Game Duration (min)"
              value={filters.gameDuration}
              onChange={(e) => onFilterChange("gameDuration", e.target.value, "Duration", e.target.value)}
              fullWidth
              InputLabelProps={{
                height: "56px",
                sx: {
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&:hover fieldset": {
                    borderColor: "#C9C9C9",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C9C9C9",
                  },
                },
              }}
            />

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  color: "#707070",
                  "&.Mui-focused": { color: "#707070" },
                }}
              >
                Manufacturer Country
              </InputLabel>
              <ClosableSelect
                label="Manufacturer Country"
                value={filters.manufacturerCountry}
                onChange={(e) => {
                  const id = e.target.value;
                  const country = countriesList.find(c => c.manufacturerCountryId === id);
                  onFilterChange("manufacturerCountry", id, "Country", country?.name);
                }}
              >
                {countriesList.map(country => (
                  <MenuItem key={country.manufacturerCountryId} value={country.manufacturerCountryId}>
                    {country.name}
                  </MenuItem>
                ))}
              </ClosableSelect>
            </FormControl>
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ width: "calc(100% - 20px)", px: 0, mb: 2 }} />

      {/* Tags */}
      <Accordion
        expanded={expanded.tags}
        onChange={handleToggle("tags")}
        disableGutters
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
          backgroundColor: "#F9F9F9",
          "@media (max-width: 856px)": {
            backgroundColor: "#FFF",
          },
        }}
      >
        <AccordionSummary
          sx={{ mt: 2 }}
          expandIcon={
            expanded.tags ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
          sx={{
            paddingLeft: 0,
          }}
        >
          <Typography fontWeight={700}>Tags</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingLeft: 0,
            "@media (max-width: 856px)": {
              pt: 0,
            },
          }}
        >
        <Stack spacing={2}>
          <Typography sx={{fontSize: "12px"}}>Enter a word and hit the Enter key on your keyboard </Typography>
          {/* Input field */}
          <TagInput filters={filters} onFilterChange={onFilterChange} />
        </Stack>
        </AccordionDetails>
      </Accordion>
      <Divider sx={{ width: "calc(100% - 20px)", px: 0, mb: 0.5 }} />

      <Button
        fullWidth
        variant="contained"
        sx={{
          mt: 2,
          mb: 5,
          width: "calc(100% - 20px)",
          backgroundColor: "#EF5658",
        }}
        onClick={() => {
          console.log("selectedCat",selectedCat);
          console.log("selectedSubCat",selectedSubCat);
          console.log(JSON.stringify(filters) === JSON.stringify(FILTER_DEFAULTS))
          if (JSON.stringify(filters) === JSON.stringify(FILTER_DEFAULTS) && !selectedCat && !selectedSubCat) {
            setPopupConfig({
              title: `Please select at least one filter to search.`,
              message: "",
              showCheckCircle: false,
              confirmText: "OK",
              cancelText: null,
              onConfirm: null,
            });
            setShowPopup(true);
            return;
          }
          handleFilter(filters, selectedChips);
        }}
      >
        APPLY
      </Button>
    </Box>
  );
}

export default function SearchResultsPage() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size
  const [selectedCat, setSelectedCat] = useState("");
  const [selectedSubCat, setSelectedSubCat] = useState(null);
  const [openSubCats, setOpenSubCats] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [contentType, setContentType] = useState("All");
  const [products, setProducts] = useState([]);
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    productId: null,
    productTitle: "",
  });

  let loggedUserName = localStorage.getItem("userName")
              ? localStorage.getItem("userName")
              : null;
  //remove from collection by sami
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    productId: 0,
    title: "",
});
  
  //add to collection by sami
  const [successPopup, setSuccessPopup] = useState({
    open: false,
    productId: 0,
    title: "",
});

  //Sami
  const [createCollection] = useMutation(CREATE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteCollection] = useMutation(DELETE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });
  const [numberofSearchResults, setNumberofSearchResults] = useState(null);
  const [categoriesMap, setCategoriesMap] = useState([]);
  const [fetchCategoriesList] = useManualQuery(CATEGORY_QUERY, {
    fetchPolicy: "network-only",
  });
  const getCategoriesList = async () => {
      console.log("getCategoriesList");
      const {
        loading: categoriesListLoading,
        error: categoriesListError,
        data: categoriesListData,
      } = await fetchCategoriesList();

      if (!categoriesListLoading && categoriesListData && categoriesListData.categories) {
        setCategoriesMap(categoriesListData.categories);
      }
      if(categoriesListError){
        console.log(categoriesListError)
      }
  };

  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || ""); //used for initial search
  const [updatedQuery, setUpdatedQuery] = useState(query);  // used for displaying search keyword and updating it and filtering
  const queryWords = (updatedQuery || "").trim().split(/\s+/).filter(Boolean);
  const [searchResults, setSearchResults] = useState([]);

  const [fetchSearchResults] = useManualQuery(SEARCH_TOYS, {
      variables: {
        searchKeyword: query,
      },
    skip: !query,
  });

  const getSearchResultsList = async () => {
    if(query){
      console.log("getSearchResultsList");
      setNumberofSearchResults(null);
      setProducts([]);
      const {
        loading: searchLoading,
        error: searchError,
        data: searchData,
      } = await fetchSearchResults();

      if (!searchLoading && searchData && searchData.searchResults) {
        const newProducts = generateProducts(searchData.searchResults);
        setProducts(newProducts);
        setNumberofSearchResults(newProducts.length);
      }
      if(searchError){
        console.log(searchError)
      }
    }
  };

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
     if (!query || query.trim() === "") {
      navigate("/invalid-page", { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getSearchResultsList();
    getCategoriesList();
  }, [query]);

  const [bookmarkedItems, setBookmarkedItems] = useState(
    products.reduce((acc, p) => ({ ...acc, [p.id]: p.isInCollection }), {})
  );
  // global filter state
  const [filters, setFilters] = useState(FILTER_DEFAULTS);

  const [selectedChips, setSelectedChips] = useState([]);
  const [appliedChips, setAppliedChips] = useState([]);

  const isMultiWord = appliedChips.length > 0 || queryWords.length > 1;

  const [applyFilters, filterState] = useMutation(FILTER_TOYS);

  const [showPopup, setShowPopup] = useState(false);
  const [popupConfig, setPopupConfig] = useState({
    title: "",
    message: "",
    showCheckCircle: false,
    confirmText: "Close",
    cancelText: "CANCEL",
    onConfirm: null,
  });

  const handleFilter = async (overrideFilters = null, overrideChips = null, overrideCat = null, overrideSubcat = null, overrideKeyword = null) => {
    console.log("handleFilter",filters);
    let finalCat = '';
    if(selectedCat)
      finalCat = selectedCat.categoryId;
    if(overrideCat)
      finalCat = '';
    let finalSubcat = '';
    if(selectedSubCat)
      finalSubcat = selectedSubCat.subcategoryId;
    if(overrideSubcat)
      finalSubcat = '';

    setNumberofSearchResults(null);
    const finalFilters = overrideFilters || filters;      // Use override OR current state
    const finalChips = overrideChips || selectedChips;
      const finalKeyword = overrideKeyword ?? updatedQuery;
      setFilters(finalFilters);
      const { data, error } = await applyFilters({
        variables: {
          filters: finalFilters,
          selectedSubCat: finalSubcat,
          selectedCat: finalCat,
          keyword: finalKeyword
        }
      });
      console.log("finalCat",finalCat)
      if (data?.filterToys?.searchResults) {
        const newProducts = generateProducts(data.filterToys.searchResults);
        setProducts(newProducts);
        setAppliedChips(finalChips);
        console.log("setNumberofSearchResults",newProducts.length);
        setNumberofSearchResults(newProducts.length);
        console.log("finalChips",finalChips);
        console.log("activeCategory",activeCategory);
      }
      if(error){
        console.log(error)
      }
      window.scrollTo(0, 0);
  };

  const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/user/${user.userUuid}/profile`);
  };

  const handleUserFollowUpdate = (newIsFollowing) => {
    // Update user data in products state
    setProducts(prev =>
      prev.map(product => ({
        ...product,
        userId: {
          ...product.userId,
          isFollowing: newIsFollowing,
        },
      }))
    );
  };

  const handleFilterChange = (name, value, group, label) => {
    if(name === "brand")
      setFilters((prev) => ({...prev, [name]: value, [`${name}Label`]: label}));
    else
      setFilters((prev) => ({ ...prev, [name]: value }));

    if(name === "price"){
      if(value !== "All Prices"){
        let prices = value.split("-");
        setFilters((prev) => ({ ...prev, ["minPrice"]: prices[0] }));
        setFilters((prev) => ({ ...prev, ["maxPrice"]: prices[1] }));
      }
      else{
        setFilters((prev) => ({ ...prev, ["minPrice"]: "" }));
        setFilters((prev) => ({ ...prev, ["maxPrice"]: "" }));
      }
    }
    if (name === "minPrice" || name === "maxPrice") {
      const newMin = name === "minPrice" ? value : filters.minPrice;
      const newMax = name === "maxPrice" ? value : filters.maxPrice;
      if (newMin && newMax)
        label = `$${newMin} to $${newMax}`;
      if (newMin && !newMax)
        label = `$${newMin} to -`;
      if (!newMin && newMax)
        label = `- to $${newMax}`;
    }
    if (
      (name === "brand" && value.length === 0) ||
      (name === "tags" && value.length === 0) ||
       label === "" ||
       label === null ||
       label === "Don't Care" ||
       label === "All Prices"
    ) {
      // remove all chips of this group
      setSelectedChips(prev => prev.filter(ch => ch.group !== group));
      return;
    }
    if (label) {
      setSelectedChips(prev => {
      // Remove previous chip of same filter (e.g. warnings)
        const filtered = prev.filter(ch => ch.group !== group);
        return [
          ...filtered,
          { group: group, label, name, value }
        ];
      });
    }
  };

  const handleCatSelect = (category) => {
    console.log("handleCatSelect");
    // If user clicks a different category, reset everything
    if (category !== selectedCat) {
      setSelectedCat(category);
      setSelectedSubCat(null); // clear old subcategory
    }
    setSelectedChips(prev => {
      let updated = prev.filter(ch => ch.group !== "Category" && ch.group !== "Subcategory");

      if (category) {
        updated.push({
          group: "Category",
          label: category.title,
          name: "category",
          value: category.categoryId
        });
      }
      return updated;
    });
  };

  const handleSubCatSelect = (category, subcategory) => {
    console.log("handleSubCatSelect");
    setSelectedCat(category);
    setSelectedSubCat(subcategory);

    setSelectedChips(prev => {
      // REMOVE Category + Subcategory chips only
      let updated = prev.filter(ch =>
        ch.group !== "Category" &&
        ch.group !== "Subcategory"
      );

      // ADD Category chip
      if (category?.title) {
        updated.push({
          group: "Category",
          label: category.title,
          name: "category",
          value: category.categoryId
        });
      }

      // ADD Subcategory chip
      if (subcategory?.title) {
        updated.push({
          group: "Subcategory",
          label: subcategory.title,
          name: "subCategory",
          value: subcategory.subcategoryId
        });
      }
      return updated;
    });
  };

  const handleContentTypeChange = (cntType) => {
    setContentType(cntType);
  };

  // Filter products based on content type
  const getFilteredProducts = () => {
    if (contentType === "All") {
      return products;
    }
    return products.filter((product) => product.contentType === contentType);
  };

  const filteredProducts = getFilteredProducts();

  // Handle share click to increment share count
  const handleShareClick = (productId) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === productId
          ? { ...product, shares: (product.shares || 0) + 1 }
          : product
      )
    );
  };
  
  // Handle bookmark click to show confirmation popup
  const handleBookmarkClick = (productId, isInCollection, contentType) => {
    const product = products.find((p) => p.id === productId);
    if(contentType === "Stories")
      contentType = "story"
    else if(contentType === "Videos")
      contentType = "video"
    else
      contentType = "toy"
    if (isInCollection) {
    // Show removal confirmation 
    setConfirmPopup({
      open: true,
      productId: productId,
      title: product ? product.title : "",
      contentType: contentType
    });
  } else {
    // Show add alert
    setSuccessPopup({
      open: true,
      productId: productId,
      title: product ? product.title : "",
      contentType: contentType
    }); 
   }  
  };

  const handleConfirmAdd = async () => {
    console.log("handleConfirmAdd");
    await createCollection({
      variables: {
        objectId: parseInt(successPopup.productId),
        contentType: successPopup.contentType,
      },
    });
    setSuccessPopup({ open: false,productId: 0, title: "" });
    console.log(successPopup.productId);
    console.log(successPopup.contentType);
    setProducts((prev) =>
      prev.map((product) =>
        product.id === successPopup.productId
          ? { ...product, isInCollection: !product.isInCollection }
          : product

      ));
    
};

  const handleConfirmRemove = async () => {
    console.log("handleConfirmRemove");
    await deleteCollection({
      variables: {
        objectId: parseInt(confirmPopup.productId),
        contentType: confirmPopup.contentType,
      },
    });

    setProducts((prev) =>
      prev.map((product) =>
        product.id === confirmPopup.productId
          ? { ...product, isInCollection: !product.isInCollection }
          : product

      ));
    setConfirmPopup({ open: false,productId: 0, title: "" });
};

  // Handle cancel action
  const handleCancelAction = () => {
    setConfirmPopup({ open: false,productId: 0, title: "" });
    setSuccessPopup({ open: false,productId: 0, title: "" });
  };

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  useEffect(() => {
    // try to target common root elements; fallback to body
    const rootEl =
      document.getElementById("root") ||
      document.getElementsByTagName("main")[0] ||
      document.body;
    if (drawerOpen) {
      // store current scroll
      const scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.dataset.scrollY = String(scrollY);

      // lock scrolling and preserve visual position
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      if (rootEl) rootEl.style.overflow = "hidden";

      // preserve scroll offset so user doesn't jump
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    } else {
      // restore
      const prev = Number(document.body.dataset.scrollY || 0);
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (rootEl) rootEl.style.overflow = "";
      window.scrollTo(0, prev);
      delete document.body.dataset.scrollY;
    }

    // cleanup if component unmounts
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (rootEl) rootEl.style.overflow = "";
      delete document.body.dataset.scrollY;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!isMobile && drawerOpen) {
      setDrawerOpen(false); // force close drawer when leaving mobile
    }
  }, [isMobile, drawerOpen]);

  const handleRemoveChip = (chipToRemove) => {
    setProducts([]);
    console.log("handleRemoveChip");
    let updatedFilters;
    let updatedSelectedChips;
    let updatedCat = null;
    let updatedSubcat = null;

    if(chipToRemove.name === "category") {
      updatedCat = chipToRemove.value;
      setSelectedCat(null);
      setActiveCategory(null);
    }

    if(chipToRemove.name === "subCategory") {
      updatedSubcat = chipToRemove.value;
      setSelectedSubCat(null);
    }

    setFilters(prev => {
      updatedFilters = { ...prev };
      if (Array.isArray(prev[chipToRemove.name])) {
        // MULTI SELECT (brand, etc.)
        updatedFilters[chipToRemove.name] = prev[chipToRemove.name].filter(
          v => v !== chipToRemove.value
        );
        const labelKey = `${chipToRemove.name}Label`;
        if (Array.isArray(updatedFilters[labelKey])) {
          updatedFilters[labelKey] = updatedFilters[labelKey].filter(
            (lbl) => lbl !== chipToRemove.label
          );
        }
      } else {
        // SINGLE SELECT
        updatedFilters[chipToRemove.name] = FILTER_DEFAULTS[chipToRemove.name];
        if (chipToRemove.name === "price") {
          updatedFilters.minPrice = "";
          updatedFilters.maxPrice = "";
        }
      }
      return updatedFilters;
    });
    // --- 2. Update selected chips ---
    setSelectedChips(prev => {
      updatedSelectedChips = prev.map(ch => {
        if (ch.name !== chipToRemove.name) return ch;

        if (Array.isArray(ch.value)) {
          // multi-select: remove only matching value
          const newValues = ch.value.filter(v => v !== chipToRemove.value);
          const newLabels = ch.label.filter(l => l !== chipToRemove.label);

          if (newValues.length === 0) return null; // remove chip group

          return { ...ch, value: newValues, label: newLabels };
        }

        return null; // single select → remove entire chip
      }).filter(Boolean);

      handleFilter(updatedFilters, updatedSelectedChips, updatedCat, updatedSubcat);

      return updatedSelectedChips;
    });
    //setAppliedChips(updatedSelectedChips);
  };

const renderSelectedFilters = () => {
  if (!appliedChips.length) return null;
  const isQueryEmpty = !updatedQuery || updatedQuery.trim() === "";
  const isQueryWordsEmpty = queryWords.length === 0;
  const isSingleAppliedChip = appliedChips.length === 1;

  // This is now 100% accurate after any deletion
  const isSingleUnremovable =
    isSingleAppliedChip && isQueryEmpty && isQueryWordsEmpty;

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={1}
      sx={{ mb: 2 }}
    >
      {appliedChips.map((chip, index) => {
        return (
          <React.Fragment key={index}>
            {(index === 0 ||
              chip.group !== appliedChips[index - 1].group) && (
              <>
                {index > 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    px={1}
                  >
                    |
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  {chip.group}:
                </Typography>
              </>
            )}

            {Array.isArray(chip.label)
              ? chip.label.map((lbl, i) => {
                  const valueForLabel = Array.isArray(chip.value)
                    ? chip.value[i]
                    : chip.value;

                  return (
                    <Pill
                      key={i}
                      label={lbl}
                      onDelete={
                        isSingleUnremovable
                          ? undefined
                          : () =>
                              handleRemoveChip({
                                ...chip,
                                label: lbl,
                                value: valueForLabel
                              })
                      }
                    />
                  );
                })
              : (
                <Pill
                  label={chip.label}
                  onDelete={
                    isSingleUnremovable
                      ? undefined
                      : () => handleRemoveChip(chip)
                  }
                />
              )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};


  const handleRemoveQueryWord = (wordToRemove) => {
    setProducts([]);
    const updatedWords = updatedQuery.split(" ").filter(w => w !== wordToRemove);
    const newQuery = updatedWords.join(" ");

    setUpdatedQuery(newQuery);
    window.history.pushState({}, "", `/search-results?q=${encodeURIComponent(newQuery)}`);

    handleFilter(filters, selectedChips, null, null, newQuery);
  };

  return (
    <AuthWrapper>
      <Helmet>
        <title>R3PLAY - Search Results</title>
        <meta
          name="description"
          content="Buy toys, sell toys, trade toys, replay toys, share stories about toys"
        />
        <meta
          property="keywords"
          content="Replay toys, resell toys, trade toys, tell stories about toys, toys social commerce, used toys commerce, collectible toys, nostalgia toys, nostalgic toys, share toy stories, share toy memories"
        />
      </Helmet>
      <Box
        sx={{
          width: "100%",
          overflowX: "hidden",
        }}
      >
        {/* Content */}
        <Box
          sx={{
            backgroundColor: "#F9F9F9",
            minHeight: "calc(100vh - 200px)",
            pt: 1,
          }}
        >
          <Box
            sx={{
              px: "100px",
              display: "flex",
              "@media (max-width: 1360px)": {
                px: "16px",
              },
              "@media (max-width: 856px)": {
                px: "16px",
              },
            }}
          >
            {/* Sidebar – visible on md+ and on tablet (sm+) per screenshot */}
            <Box sx={{ display: isMobile ? "none" : "flex" }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 0,
                  boxShadow: "none",
                  flex: 1,
                  width: "296px",
                  "@media (max-width: 1360px)": {
                    width: "260px",
                  },
                }}
              >
                <FiltersContent
                  onCatSelect={handleCatSelect}
                  onSubCatSelect={handleSubCatSelect}
                  selectedCat={selectedCat}
                  selectedSubCat={selectedSubCat}
                  activeCategory={activeCategory}
                  setActiveCategory={setActiveCategory}
                  openSubCats={openSubCats}
                  setOpenSubCats={setOpenSubCats}
                  categoriesMap={categoriesMap}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onContentTypeChange={handleContentTypeChange}
                  setProducts={setProducts}
                  handleFilter={handleFilter}
                  setAppliedChips={setAppliedChips}
                  selectedChips={selectedChips}
                  setNumberofSearchResults={setNumberofSearchResults}
                  setPopupConfig={setPopupConfig}
                  setShowPopup={setShowPopup}
                />
              </Paper>
              {/* Vertical divider between sidebar and results */}
              <Divider orientation="vertical" flexItem />
            </Box>

            {/* Cards grid */}
            <Box
              sx={{
                width: "928px",
                pl: "16px",
                "@media (max-width: 1360px)": {
                  width: "526px",
                },
                "@media (max-width: 856px)": {
                  width: "393px",
                  pl: "10px",
                },
              }}
            >
              {!isMobile ? (
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    my: 1,
                  }}
                >
                  Search Results for:
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      my: 1,
                    }}
                  >
                    Search Results for:
                  </Typography>
                  {/* Filter Button */}
                  <Tooltip
                    title="View search filter"
                    placement="top"
                    arrow
                    PopperProps={{
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -5],
                          },
                        },
                      ],
                    }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#666",
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "500",
                          borderRadius: "4px",
                          padding: "6px 8px",
                          "& .MuiTooltip-arrow": {
                            color: "#666",
                          },
                        },
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: "#666",
                        position: "absolute",
                        right: "16px",
                        width: 35,
                        height: 35,
                        borderRadius: "4px",
                        my: 1,
                        border: "1px solid #ddd",
                        "&:hover": {
                          backgroundColor: "#e8e8e8",
                        },
                      }}
                      onClick={() => setDrawerOpen(true)}
                    >
                      <FilterAltOutlined />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Keyword(s):
                </Typography>
                {updatedQuery && updatedQuery.trim().length > 0 && (
                  queryWords.map((word, index) => (
                    <Pill
                      key={index}
                      sx={{ mr: 1 }}
                      label={word}
                      onDelete={isMultiWord ? () => handleRemoveQueryWord(word) : undefined}
                      deleteIcon={
                        isMultiWord ? <CancelIcon /> : undefined
                      }
                    />
                  ))
                )}
                {numberofSearchResults !== null && numberofSearchResults !== undefined && (
                  <Typography sx={{ ml: 10 }}>
                    ({numberofSearchResults} items)
                  </Typography>
                )}
              </Stack>

              {renderSelectedFilters()}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  justifyContent: "left",
                  gap: "24px",
                  width: "912px",
                  pt: "1px",
                  "@media (max-width: 1360px)": {
                    width: "626px",
                    gap: "16px",
                    mb: 2,
                  },
                  "@media (max-width: 856px)": {
                    width: "361px",
                    justifyContent: "center",
                    gap: 0,
                  },
                }}
              >
                {filteredProducts.map((p, index) => {
                  const showFloatingMenu =
                    (!isTablet && !isMobile && (index + 1) % 3 === 0) || // desktop → 3rd card
                    (isTablet && (index + 1) % 2 === 0) || // tablet → 2nd card
                    (isMobile && true);

                  const fabLeftMargin =
                    (isMobile && 285) ||
                    (isTablet && 820) ||
                    (!isMobile && !isTablet && 1255);
                  return (
                    <Box
                      key={p.id}
                      sx={{
                        width: "288px",
                        position: "relative",
                        "@media (max-width: 856px)": {
                          mb: "24px",
                        },
                      }}
                    >
                      <ProductCard
                        p={p}
                        onBookmarkClick={handleBookmarkClick}
                        onUserProfileNavigation={handleUserProfileNavigation}
                        onFollowToggle={handleUserFollowUpdate}
                        onShareClick={handleShareClick}
                      />
                      {showFloatingMenu && (
                        <FloatingMenu left={fabLeftMargin} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Mobile / popup drawer for filters (exactly like the mobile screenshot) */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ disableScrollLock: false }}
          PaperProps={{
            sx: {
              width: "100%",
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
              height: "44px",
              position: "relative",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                height: "26px",
                width: "123px",
                textAlign: "center",
              }}
            >
              SEARCH FILTER
            </Typography>
            <IconButton
              onClick={toggleDrawer(false)}
              sx={{
                position: "absolute",
                right: 14,
                height: 24,
                width: 24,
              }}
            >
              <ClearOutlinedIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              width: "393px",
              pl: "16px",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                mt: 2,
                mb: 0.5,
              }}
            >
              Search Results for:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Keyword:
              </Typography>
              <Pill label="star wars" />
            </Stack>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              sx={{ mb: 1.5 }}
            >
              {/* Category */}
              <Typography variant="body2" color="textSecondary">
                Category:
              </Typography>
              {["Action Figures & Dolls"].map((cat, index) => (
                <Pill
                  key={index}
                  label={cat}
                  sx={{
                    mt: 0.5,
                    ml: 0.5,
                  }}
                />
              ))}
              <Typography variant="body2" color="textSecondary" pt={1}>
                <NavigateNextIcon fontSize="small" />
              </Typography>
              {["Superheroes"].map((subCat, index) => (
                <Pill
                  key={index}
                  label={subCat}
                  sx={{
                    mt: 0.5,
                  }}
                />
              ))}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 1.5 }}
            >
              {/* Brand */}
              <Typography variant="body2" color="textSecondary">
                Brand:
              </Typography>
              {["LEGO", "Mattel"].map((b, index) => (
                <Pill key={index} label={b} />
              ))}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: "16px" }}
            >
              {/* Age Group */}
              <Typography variant="body2" color="textSecondary">
                Age Group:
              </Typography>
              <Pill label={"2-4 Years"} />
            </Box>
          </Box>
          <FiltersContent
            onCatSelect={handleCatSelect}
            onSubCatSelect={handleSubCatSelect}
            selectedCat={selectedCat}
            selectedSubCat={selectedSubCat}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            openSubCats={openSubCats}
            setOpenSubCats={setOpenSubCats}
            categoriesMap={categoriesMap}
            filters={filters}
            onFilterChange={handleFilterChange}
            onContentTypeChange={handleContentTypeChange}
            setProducts={setProducts}
            handleFilter={handleFilter}
            setAppliedChips={setAppliedChips}
            selectedChips={selectedChips}
            setNumberofSearchResults={setNumberofSearchResults}
            setPopupConfig={setPopupConfig}
            setShowPopup={setShowPopup}
          />
        </Drawer>
      </Box>

      {/* Alert Popup for add collection actions */} 
      <AlertPopup
        open={successPopup.open}
        title={`Item "${successPopup.title}" is added to your collection.`}
        cancelText=""
        confirmText="OK"
        onConfirm={handleConfirmAdd}
        onClose={() => setSuccessPopup({ open: false, productId: 0,title: "" })}
      />

      {/* Alert Popup for remove collection actions */} 
      <AlertPopup
        open={confirmPopup.open}
        title={`Are you sure you want to remove "${confirmPopup.title}" from your collection?`}
        confirmText="Yes, Remove"
        cancelText="Cancel"
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmPopup({ open: false, productId: 0,title: "" })}
        onClose={() => setConfirmPopup({ open: false, productId: 0,title: "" })}
      />     
        <AlertPopup
          open={showPopup}
          onClose={() => setShowPopup(false)}
          title={popupConfig.title}
          message={popupConfig.message}
          showCheckCircle={popupConfig.showCheckCircle}
          confirmText={popupConfig.confirmText}
          cancelText={popupConfig.cancelText}
          onConfirm={popupConfig.onConfirm}
        />
      </AuthWrapper>
  );
}
